import {useCallback, useRef, useState} from 'react'
import {Dialog, Spinner} from '@primer/react'
import {StackIcon} from '@primer/octicons-react'
import {StackOverlay} from '@github-ui/pull-requests/components/StackOverlay'
import styles from './StackBadge.module.css'
import {useStackData} from './use-stack-data'

interface StackBadgeProps {
  pullRequestNumber: number
  stackPosition: number | null
  stackSize: number | null
  repoNameWithOwner: string
  includeDot?: boolean
}

interface StackOverlayDialogProps {
  repoNameWithOwner: string
  pullRequestNumber: number
  returnFocusRef: React.RefObject<HTMLButtonElement | null>
  onClose: () => void
}

function StackOverlayDialog({repoNameWithOwner, pullRequestNumber, returnFocusRef, onClose}: StackOverlayDialogProps) {
  const {data, isLoading, error} = useStackData(repoNameWithOwner, pullRequestNumber)
  const stack = data?.stack ?? null

  return (
    <Dialog
      title={`Stack ${stack ? `#${stack.number}` : ''}`}
      width="large"
      returnFocusRef={returnFocusRef}
      onClose={onClose}
      renderBody={({children}) => <Dialog.Body className="p-0">{children}</Dialog.Body>}
    >
      {isLoading && (
        <div className="d-flex flex-justify-center flex-items-center p-4">
          <Spinner size="medium" />
        </div>
      )}
      {error && (
        <div className="d-flex flex-justify-center flex-items-center p-4">
          {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
          <p className="text-center color-text-danger">Failed to load stack data.</p>
        </div>
      )}
      {stack && (
        <StackOverlay
          stack={stack}
          pullRequestNumber={pullRequestNumber}
          baseBranchText={stack.baseBranch}
          showStackMutationButtons={false}
        />
      )}
    </Dialog>
  )
}

export function StackBadge({
  pullRequestNumber,
  stackPosition,
  stackSize,
  repoNameWithOwner,
  includeDot = true,
}: StackBadgeProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const badgeRef = useRef<HTMLButtonElement>(null)

  const handleClose = useCallback(() => {
    setIsDialogOpen(false)
  }, [])

  if (stackPosition === null || stackSize === null || stackPosition === undefined || stackSize === undefined) {
    return null
  }

  return (
    <>
      {includeDot && <span aria-hidden="true"> &middot; </span>}
      <button
        type="button"
        className={styles.badge}
        onClick={() => setIsDialogOpen(true)}
        ref={badgeRef}
        aria-haspopup="dialog"
        aria-expanded={isDialogOpen}
        aria-label={`Pull request stack, position ${stackPosition} of ${stackSize}`}
      >
        <StackIcon size={16} />
        {stackPosition}/{stackSize}
      </button>
      {isDialogOpen && (
        <StackOverlayDialog
          repoNameWithOwner={repoNameWithOwner}
          pullRequestNumber={pullRequestNumber}
          returnFocusRef={badgeRef}
          onClose={handleClose}
        />
      )}
    </>
  )
}
